import { memo, useState, useRef, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { Box, Image, Select, Typography, Button as AstroButton } from '@astro-ui/components'
import { SystemIcon, Button, InputDropDown, DeviceWrapper } from 'components'
import {
  purchaseOrder,
  order,
  orderPicking,
  orderPacking,
  orderWaitingDriver,
  orderOnDelivery,
  orderBackToHub,
  orderNotAvailable,
  orderSuper,
  orderPickingSuper,
  orderPackingSuper,
  orderWaitingDriverSuper,
  orderOnDeliverySuper,
  orderBackToHubSuper,
  orderUpcoming,
  createOrder,
  driverMonitoring,
  hcu,
  poolingMap,
  orderWaitingConfirmation,
  orderReadyForDelivery,
} from 'config/routes'
import { useHistory, useLocation } from 'react-router-dom'
import handleClickOutSide from 'customHooks/outSideClick'
import logo from 'assets/images/logo-new.png'
import userAvatar from 'assets/images/avatar-user.png'

import { setSidebarOpen } from 'redux/app'
import { handleLogout } from 'features/Auth/redux/Auth'

import storeContext from 'storeContext'
import { setSelectedLocation } from 'storeContext/actions/packer'
import { transformQuerytoObj, transformObjtoQuery } from 'helpers'
import { setPopupConfirmReset } from 'features/Order/redux/CreateOrder'
import { useAppSelector, useAppDispatch, shallowEqual } from 'store'
import {
  fetchGetLocationDetail,
  setSelectedDriverLocation,
} from 'features/Order/redux/DriverMonitoring'
import { fetchLocationDetail, setLocationSelected } from 'features/Order/redux/PoolingMap'
import { OrderRouterDetail } from 'features/Order/router'
import SelectLocation from 'features/Order/pages/AllOrderV2/SelectLocation/SelectLocation'
import { Close, HamburgerMenu } from '@astronautsid/wpe-icons'
import useDeviceDetect from 'customHooks/useDeviceDetect'
import { OnGoingV2Routes } from 'features/Order/router/router'

const allowLocation = [purchaseOrder]
const checkPathToOrder = [
  order,
  orderPicking,
  orderPacking,
  orderWaitingDriver,
  orderOnDelivery,
  orderBackToHub,
  orderNotAvailable,
  orderSuper,
  orderPickingSuper,
  orderPackingSuper,
  orderWaitingDriverSuper,
  orderOnDeliverySuper,
  orderBackToHubSuper,
  orderUpcoming,
  orderWaitingConfirmation,
  createOrder,
  hcu,
  orderReadyForDelivery,
  ...Object.values(OnGoingV2Routes),
]

const selector = ({
  app: {
    layout: { isSidebarOpen },
  },
  auth,
  createOrder,
}) => ({
  isSidebarOpen,
  activeStep: createOrder.activeStep,
  full_name: auth?.userData?.full_name,
})
function Navbar({ className }) {
  const {
    state: { locations, selectedLocation },
    dispatch,
  } = useContext(storeContext.PackerContext)

  const history = useHistory()
  const dispatchRedux = useAppDispatch()
  const { isSidebarOpen, activeStep, full_name } = useAppSelector(selector, shallowEqual)
  const driverMonitoringState = useAppSelector(
    (state) => ({
      locations: state.driverMonitoring.listLocation,
      selectedLocation: state.driverMonitoring.locationSelected,
    }),
    shallowEqual,
  )
  const orderPoolingMapState = useAppSelector(
    ({ poolingMap }) => ({
      locations: poolingMap.listLocation,
      selectedLocation: poolingMap.locationSelected,
    }),
    shallowEqual,
  )

  const refDropDown = useRef(null)
  const [isShowDropDown, setIsShowDropDown] = useState(false)

  const location = useLocation()
  const { isMobile } = useDeviceDetect()

  const allowFilterLocation = useMemo(() => {
    return allowLocation.includes(location.pathname)
  }, [location.pathname])

  const allowFilterOrderLocation = useMemo(() => {
    return checkPathToOrder.includes(location.pathname)
  }, [location.pathname])

  const handleClickDropDown = () => {
    setIsShowDropDown(true)
  }

  handleClickOutSide(
    refDropDown,
    () => {
      setIsShowDropDown(false)
    },
    isShowDropDown,
  )

  const onLogout = () => {
    dispatchRedux(handleLogout())
  }

  const handleToggleSidebarExpand = (e) => {
    e.stopPropagation()
    dispatchRedux(setSidebarOpen(!isSidebarOpen))
  }

  const handleClickOption = (locationData) => {
    if (!locationData) return
    if (location.pathname.includes(createOrder)) {
      if (activeStep > 1) {
        dispatchRedux(setPopupConfirmReset(true))
      }
    }
    dispatch(setSelectedLocation(locationData))
    const queryObj = transformQuerytoObj(location.search)
    queryObj.locationId = locationData.location_id
    const query = transformObjtoQuery(queryObj)
    history.replace(query)
  }

  const handleSelectLocationDriver = async (loc) => {
    if (loc?.location_id) {
      const response = await dispatchRedux(fetchGetLocationDetail(loc.location_id)).unwrap()
      dispatchRedux(setSelectedDriverLocation(response))
    } else {
      dispatchRedux(setSelectedDriverLocation(null))
    }
  }

  const handleSelectLocationOrderPooling = async (loc) => {
    if (loc?.location_id) {
      const response = await dispatchRedux(fetchLocationDetail(loc.location_id)).unwrap()
      dispatchRedux(setLocationSelected(response))
    } else {
      dispatchRedux(setLocationSelected(null))
    }
  }

  return (
    <Container
      className={className}
      p={{
        mobile: '0 16px',
        desktop: '0 30px',
      }}
    >
      <LeftWrapper>
        {isMobile && (
          <ActionButton onClick={handleToggleSidebarExpand}>
            {isSidebarOpen ? <Close /> : <HamburgerMenu />}
          </ActionButton>
        )}

        <WrapperLogoAndVersion>
          <Logo src={logo} alt="logo" height={24} />
          <Typography
            fontSize="12px"
            fontWeight={600}
            lineHeight="16px"
            letterSpacing="3%"
            color="#646464"
          >
            v{process.env.REACT_APP_VERSION}
          </Typography>
        </WrapperLogoAndVersion>
      </LeftWrapper>

      <RightWrapper>
        {allowFilterLocation && (
          <>
            <StyledDropDown iconName="store" />
            <Devider />
          </>
        )}
        {allowFilterOrderLocation && (
          <>
            <Select
              className="ml-16"
              value={selectedLocation?.name ?? ''}
              options={locations}
              onChange={handleClickOption}
              placeholder="Pilih Hub"
              withSearch
              required
            />
            <Devider />
          </>
        )}
        {location.pathname === driverMonitoring && (
          <>
            <Select
              className="ml-16"
              value={driverMonitoringState.selectedLocation}
              options={driverMonitoringState.locations}
              onChange={handleSelectLocationDriver}
              placeholder="Pilih Hub"
              displayKey="location_name"
              withSearch
            />
            <Devider />
          </>
        )}
        {location.pathname === poolingMap && (
          <>
            <Select
              className="ml-16"
              value={orderPoolingMapState.selectedLocation}
              options={orderPoolingMapState.locations}
              onChange={handleSelectLocationOrderPooling}
              placeholder="Pilih Hub"
              displayKey="location_name"
              withSearch
            />
            <Devider />
          </>
        )}
        {location.pathname === OrderRouterDetail.AllOrderV2Router.path && <SelectLocation />}
        <Box
          display="flex"
          onClick={handleClickDropDown}
          alignItems="center"
          style={{ cursor: 'pointer' }}
        >
          <Account>
            <Image src={userAvatar} alt="user avatar" width={24} height={24} />
          </Account>
          <DeviceWrapper show={['DESKTOP']} ml="8px">
            <Typography variant="h4" color="#212121">
              {full_name}
            </Typography>
          </DeviceWrapper>
          <span>
            <SystemIcon iconName="dropdown" className="ml-5" colorIcon="#646464" />
          </span>
        </Box>

        {isShowDropDown && (
          <Actions ref={refDropDown}>
            <DeviceWrapper
              show={['MOBILE', 'TABLET']}
              p="0 12px 8px"
              borderBottom="1px solid lightgray"
              textAlign="center"
            >
              <Typography>{full_name}</Typography>
            </DeviceWrapper>
            <Button variant="wrapper" className="w-100" onClick={onLogout}>
              <Act>
                <SystemIcon iconName="logout" colorIcon="red" />
                <Typography color="red">Keluar</Typography>
              </Act>
            </Button>
          </Actions>
        )}
      </RightWrapper>
    </Container>
  )
}

Navbar.defaultProps = {
  className: '',
}

export default memo(Navbar)

export const NAVBAR_HEIGHT = 56

const WrapperLogoAndVersion = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`

const Account = styled.div`
  background-color: ${({ theme: { colors } }) => colors.main};
  border-radius: 50%;
  height: 24px;
  width: 24px;
  display: grid;
  place-items: center;
  margin-left: 16px;
  margin-right: 0px;
`

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 5px;
`

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`

const Container = styled(Box)`
  height: ${NAVBAR_HEIGHT}px;
  width: 100%;
  position: fixed;
  top: 0px;
  display: flex;
  justify-content: space-between;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background-color: ${({ theme: { colors } }) => colors.white};
  z-index: ${({ theme }) => theme.zIndex.drawer + 1};
`

const Actions = styled.div`
  border-radius: 6px;
  border: 1px solid #e4eaf3;
  box-shadow: 0px 2px 16px rgba(202, 211, 225, 0.4);
  position: absolute;
  right: 0;
  background-color: ${({ theme: { colors } }) => colors.white};
  top: 45px;
  width: 350px;
  padding: 10px;
`

const Act = styled.div`
  padding: 10px;
  display: flex;
  gap: 10px;
  cursor: pointer;
  align-items: center;
  p {
    white-space: nowrap;
  }

  :hover {
    background-color: aliceblue;
  }
`

const ActionButton = styled(AstroButton).attrs({ variant: 'text' })`
  min-width: 0;
`

const Logo = styled(Image)`
  height: 26px;
`

const StyledDropDown = styled(InputDropDown)`
  width: 150px;
  input {
    padding: 6px 0;
  }
`

const Devider = styled.div`
  border: 1px solid #d6dfeb;
  margin: 0px 15px;
  width: 1px;
  height: 25px;
`
